// The Automation panel's seal and potion lists. Each is an ordered list of
// the items of one kind, each switched on or off. The hunt uses the first item
// that is on and in the bag, so "best until they run out, then the cheap ones"
// is the best on top, and "only the best" is every other row switched off.
// Nothing here ever buys: the player keeps the bag stocked.
import { CONFIG } from './config.ts';
import { ITEMS, ITEM } from './items.ts';
import { gradeFloor, gradeOf as gradeOfScore, type Grade } from './grade.ts';
import { ratingOf, statWeights } from './rating.ts';
import { ivPercent } from './rolls.ts';
import { SPECIES } from './species.ts';
import { isCatchSound } from './golden.ts';
import type { Rolls } from './rolls.ts';
import type { CreatureDTO, Settings, TrainerDTO } from './protocol.ts';

export interface UseRow { id: string; on: boolean }
export type UseKind = 'seals' | 'potions';

/** Every seal and every potion, weakest first. */
export const USE_IDS: Record<UseKind, readonly string[]> = {
  seals: ITEMS.filter((i) => i.kind === 'seal').sort((a, b) => (a.catchMult ?? 1) - (b.catchMult ?? 1)).map((i) => i.id),
  potions: ITEMS.filter((i) => i.kind === 'potion').sort((a, b) => (a.heal ?? 0) - (b.heal ?? 0)).map((i) => i.id),
};

/** "Heal below" choices, in percent of max HP. */
export const POTION_BELOW_CHOICES = [30, 40, 50, 70] as const;
export const DEFAULT_POTION_BELOW = Math.round(CONFIG.autoPotionBelow * 100);

/** New trainers: the cheapest seal first, the strongest potion first, everything on. */
export const DEFAULT_SEALS: UseRow[] = USE_IDS.seals.map((id) => ({ id, on: true }));
export const DEFAULT_POTIONS: UseRow[] = [...USE_IDS.potions].reverse().map((id) => ({ id, on: true }));

/**
 * A list from the client or a save, cleaned: only real ids of the kind, each
 * once, `on` a boolean. Items the list leaves out go to the end, on. Null
 * when it isn't a list at all.
 */
export function cleanOrder(raw: unknown, kind: UseKind): UseRow[] | null {
  if (!Array.isArray(raw)) return null;
  const ids = USE_IDS[kind];
  const out: UseRow[] = [];
  for (const r of raw.slice(0, 32)) {
    if (!r || typeof r !== 'object') continue;
    const { id, on } = r as { id?: unknown; on?: unknown };
    if (typeof id !== 'string' || !ids.includes(id) || out.some((x) => x.id === id)) continue;
    out.push({ id, on: on !== false });
  }
  for (const id of ids) if (!out.some((x) => x.id === id)) out.push({ id, on: true });
  return out;
}

/** This seal first and on, the rest as they were (the shop's "Use for catching"). */
export function toTop(rows: readonly UseRow[], id: string): UseRow[] {
  return [{ id, on: true }, ...rows.filter((r) => r.id !== id)];
}

/** The same rows, sorted by strength. */
export function sortByStrength(rows: readonly UseRow[], kind: UseKind, strongestFirst: boolean): UseRow[] {
  const rank = (id: string) => USE_IDS[kind].indexOf(id);
  return [...rows].sort((a, b) => (strongestFirst ? rank(b.id) - rank(a.id) : rank(a.id) - rank(b.id)));
}

/** The first item that is on and in the bag. */
export function firstInStock(rows: readonly UseRow[], bag: Record<string, number>): string | null {
  return rows.find((r) => r.on && (bag[r.id] ?? 0) > 0)?.id ?? null;
}

/** Whether Auto potion would drink something from this bag: it is on and a ticked potion is in stock (the faint stop, server/hunt.ts). */
export function drinksPotion(s: { autoPotion: boolean; potions: readonly UseRow[] }, bag: Record<string, number>): boolean {
  return s.autoPotion && firstInStock(s.potions, bag) !== null;
}

/**
 * Settings as saved before the lists existed (or by an older client), brought
 * up to date: the old single seal goes first in the seal list, the old
 * auto-buy amount is dropped, and missing fields get their defaults.
 */
export function normalizeSettings(s: Settings): Settings {
  const legacy = s as Settings & { seal?: string; autoBuySeals?: unknown };
  const seals = cleanOrder(s.seals, 'seals') ?? (legacy.seal && ITEM[legacy.seal]?.kind === 'seal' ? toTop(DEFAULT_SEALS, legacy.seal) : DEFAULT_SEALS.map((r) => ({ ...r })));
  const potions = cleanOrder(s.potions, 'potions') ?? DEFAULT_POTIONS.map((r) => ({ ...r }));
  const out: Settings = { ...s, seals, potions, favAuras: s.favAuras !== false, favBloodline: s.favBloodline === true, favGrade: isFavGrade(s.favGrade) ? s.favGrade : 'off', potionBelow: (POTION_BELOW_CHOICES as readonly number[]).includes(s.potionBelow) ? s.potionBelow : DEFAULT_POTION_BELOW,
    // Sounds: the Golden Wild chime starts on, the catch chime off (Cole, 2026-10-27).
    goldenSound: s.goldenSound !== false, catchSound: isCatchSound(s.catchSound) ? s.catchSound : 'off' };
  delete (out as { seal?: string }).seal;
  delete (out as { autoBuySeals?: unknown }).autoBuySeals;
  // catchAuras (Always throw at Auras) went when the Aura moved from the spawn to the catch.
  delete (out as { catchAuras?: unknown }).catchAuras;
  // Sell junk went with the junk loot (2026-10-16).
  delete (out as { autoSellJunk?: unknown }).autoSellJunk;
  return out;
}

/** Healing per potion the analyser should count: only the potions the hunt will drink, and no more than fits above the threshold. */
export function usableHeals(s: Settings): Record<string, number> {
  if (!s.autoPotion) return {};
  const room = 1 - s.potionBelow / 100;
  return Object.fromEntries(s.potions.filter((r) => r.on).map((r) => [r.id, Math.min(ITEM[r.id]?.heal ?? 0, room)]));
}

// Auto-favorite: catches that match a rule go straight to favorites. Rules only
// add; switching one off never takes a favorite away.

/** Grades the "at or above" rule and the bulk release accept (C would mean everything). */
export const FAV_GRADES = ['B', 'A', 'S', 'S+'] as const satisfies readonly Grade[];
export type FavGrade = (typeof FAV_GRADES)[number];
export const isFavGrade = (v: unknown): v is FavGrade => (FAV_GRADES as readonly unknown[]).includes(v);
/** Bloodline index of III. */
const BL_III = 2;
/** Talent percents and Quality multipliers the "at or above" rules accept. */
export const FAV_TALENTS = [90, 95, 100] as const;
export const FAV_QUALITIES = [1.1, 1.15, 1.18] as const;

/** Whether the player's rules send this catch to favorites. */
export function autoFavorite(s: Settings, rolls: Rolls, species: string): boolean {
  if (rolls.aura && s.favAuras !== false) return true;
  if (rolls.bloodline >= BL_III && s.favBloodline) return true;
  if (s.favLegendary && SPECIES[species]?.rarity === 'legendary') return true;
  if (s.favTalent && ivPercent(rolls, statWeights(species)) >= s.favTalent) return true;
  if (s.favQuality && rolls.quality >= s.favQuality - 1e-9) return true;
  return isFavGrade(s.favGrade) && ratingOf(rolls, species) >= gradeFloor(s.favGrade);
}

/** A new creature (catch, hatch, boss bind): into favorites when a rule matches. */
export function applyAutoFavorite(t: TrainerDTO, c: CreatureDTO) {
  if (autoFavorite(t.settings, c.rolls, c.species) && !t.collection?.includes(c.id)) (t.collection ??= []).push(c.id);
}

/**
 * Who "release all non-favorites below grade X" takes: graded below `grade`, and
 * never the team, a favorite, a listing, an Aura, a shrine post, a charm holder or the starter.
 */
export function releasableBelow(t: TrainerDTO, creatures: Iterable<CreatureDTO>, grade: FavGrade): CreatureDTO[] {
  const floor = gradeFloor(grade);
  const keep = keptIds(t);
  const out: CreatureDTO[] = [];
  for (const c of creatures) if (!isKept(keep, c) && ratingOf(c.rolls, c.species) < floor) out.push(c);
  return out;
}

/** What bulk release and the Altar always keep, as the dialogs name it (keptIds and isKept). */
export const KEPT_WHAT = 'favorites, team, starter, for sale, Auras, shrine posts, XP Charm holders';

/** Ids no bulk action may take: the team, favorites, XP Charm holders and shrine posts. */
export function keptIds(t: TrainerDTO): Set<number> {
  return new Set([...t.team, ...(t.collection ?? []), ...(t.charmed ?? []), ...(t.shrines ?? []).flatMap((x) => x.posts)]);
}

/** Protected from bulk release and the Altar: a kept id (keptIds), a listing, an Aura or the starter. */
export function isKept(keep: ReadonlySet<number>, c: CreatureDTO): boolean {
  return keep.has(c.id) || c.listed || c.bound || c.rolls.aura;
}

/** Grades in order, worst first: the bulk release's "up to" choices. */
export const GRADE_ORDER: readonly Grade[] = ['C', 'B', 'A', 'S', 'S+'];

/**
 * The bulk release (Creatures > Release…): which species, graded at most `upTo` ('all' = any grade), beyond the best
 * `keep` of each species, optionally keeping Bloodline III and up. Never a kept creature (isKept: team, favorites,
 * starter, listings, Auras, shrine posts, XP Charm holders).
 */
export interface BulkRelease { species: ReadonlySet<string> | 'all'; upTo: Grade | 'all'; keep: number; keepBl3: boolean }
export function bulkReleaseList(t: TrainerDTO, creatures: Iterable<CreatureDTO>, o: BulkRelease): CreatureDTO[] {
  const kept = keptIds(t);
  const max = o.upTo === 'all' ? Infinity : GRADE_ORDER.indexOf(o.upTo);
  const bySpecies = new Map<string, { c: CreatureDTO; score: number }[]>();
  for (const c of creatures) {
    if (o.species !== 'all' && !o.species.has(c.species)) continue;
    const l = bySpecies.get(c.species) ?? [];
    l.push({ c, score: ratingOf(c.rolls, c.species) });
    bySpecies.set(c.species, l);
  }
  const out: CreatureDTO[] = [];
  for (const list of bySpecies.values()) {
    // The best of each species are the ones kept: highest score, then level, then the oldest.
    list.sort((a, b) => b.score - a.score || b.c.level - a.c.level || a.c.id - b.c.id);
    list.forEach(({ c, score }, i) => {
      if (i < o.keep || isKept(kept, c)) return;
      if (o.keepBl3 && c.rolls.bloodline >= BL_III) return;
      const g = GRADE_ORDER.indexOf(gradeOfScore(score));
      if (g <= max) out.push(c);
    });
  }
  return out;
}
